import { Controller, useFormContext } from 'react-hook-form';

import { Flex, Select, TextArea } from '@spazious/dollhouse-uikit';
import { isInputElement } from '@spazious/dollhouse-utils';
import {
  CreateBookingPlannerRfpRequestDataAttributesAccountTypeEnum as AccountTypeEnum,
  CreateBookingPlannerRfpRequestDataAttributesSegmentationTypeEnum as SegmentationTypeEnum,
} from '@spazious/react-booking-engine';
import { useEventCallback } from '@spazious/react-hooks';

import { getSelectionOptionsFromEnum } from '../../utils/index.js';
import { DatePicker } from '../DatePicker/index.js';
import { InputText } from '../InputText/index.js';

import { ErrorMessage } from './ErrorMessage.js';
import { Label } from './Label.js';
import { ReservationFormValues } from './useReservationForm.js';

const accountTypeOptions = getSelectionOptionsFromEnum(AccountTypeEnum);

const segmentationTypeOptions = getSelectionOptionsFromEnum(SegmentationTypeEnum);

export interface ReservationFormProps {
  onSubmit: (arg: any) => void;
}

export const ReservationForm: React.FC<ReservationFormProps> = ({ onSubmit }) => {
  const {
    handleSubmit,
    register,
    control,
    watch,
    formState: { errors },
  } = useFormContext<ReservationFormValues>();

  const setInputAttributeRef = useEventCallback<
    React.RefCallback<Record<string, React.ReactInstance>>
  >((node) => {
    if (isInputElement(node?.input)) {
      node.input.setAttribute('inputmode', 'none');
    }
  });

  return (
    <form noValidate onSubmit={handleSubmit(onSubmit)}>
      <Flex
        column
        grow
        gap="calc(var(--spacing-gap) * 2)"
        padding="calc(var(--spacing-padding)* 3)"
      >
        <Flex alignItems="flex-start" flexWrap="wrap" gap="calc(var(--spacing-gap) * 2)">
          <Flex column gap="var(--spacing-gap)">
            <Label label="Proposal name" />
            <InputText
              style={{ minWidth: '225px' }}
              {...register('proposalName', { required: 'Proposal Name required' })}
            />
            <ErrorMessage message={errors.proposalName?.message?.toString()} />
          </Flex>
          <Flex column gap="var(--spacing-gap)">
            <Label label="Email address" />
            <InputText
              style={{ minWidth: '225px' }}
              {...register('email', {
                pattern: {
                  value: /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+\.[a-zA-Z0-9-]+$/,
                  message: 'Invalid email format',
                },
                required: 'Email required',
              })}
            />
            <ErrorMessage message={errors.email?.message?.toString()} />
          </Flex>
        </Flex>
        <Flex alignItems="flex-start" flexWrap="wrap" gap="calc(var(--spacing-gap) * 2)">
          <Flex column gap="var(--spacing-gap)">
            <Label label="Phone number" />
            <InputText
              style={{ minWidth: '225px' }}
              type="tel"
              {...register('phoneNumber', {
                required: 'Phone number required',
                pattern: {
                  value: /^[0-9]+$/,
                  message: 'Invalid phone number, only numbers',
                },
                minLength: {
                  value: 6,
                  message: 'Invalid min length',
                },
              })}
            />
            <ErrorMessage message={errors.phoneNumber?.message?.toString()} />
          </Flex>
        </Flex>
        <Flex alignItems="flex-start" flexWrap="wrap" gap="calc(var(--spacing-gap) * 2)">
          <Flex column gap="var(--spacing-gap)">
            <Label label="Account Type" />
            <Select
              style={{ minWidth: '225px' }}
              {...register('accountType', {
                required: 'Account type required',
              })}
            >
              <Select.Option value="">Select...</Select.Option>
              {accountTypeOptions.map(({ value, label }) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
            <ErrorMessage message={errors.accountType?.message?.toString()} />
          </Flex>
          <Flex column gap="var(--spacing-gap)">
            <Label label="Account name" />
            <InputText
              style={{ minWidth: '225px' }}
              {...register('accountName', {
                required: 'Account name required',
              })}
            />
            <ErrorMessage message={errors.accountName?.message?.toString()} />
          </Flex>
        </Flex>

        <Flex alignItems="flex-start" flexWrap="wrap" gap="calc(var(--spacing-gap) * 2)">
          <Flex column flex="1" gap="var(--spacing-gap)">
            <Label label="Event Type" />
            <Select
              style={{ minWidth: '225px' }}
              {...register('segmentationType', {
                required: 'Event Type required',
              })}
            >
              <Select.Option value="">Select...</Select.Option>
              {segmentationTypeOptions.map(({ value, label }) => (
                <Select.Option key={value} value={value}>
                  {label}
                </Select.Option>
              ))}
            </Select>
            <ErrorMessage message={errors.segmentationType?.message?.toString()} />
          </Flex>
          <Flex column flex="1" gap="var(--spacing-gap)">
            <Label label="Participants" />
            <InputText
              min="1"
              style={{ minWidth: '225px' }}
              type="number"
              {...register('attendees', {
                required: 'Number of participants required',
                min: {
                  value: 1,
                  message: 'At least 1 participants required',
                },
              })}
            />
            <ErrorMessage message={errors.attendees?.message?.toString()} />
          </Flex>
        </Flex>

        <Flex alignItems="flex-start" flexWrap="wrap" gap="calc(var(--spacing-gap) * 2)">
          <Flex column gap="var(--spacing-gap)">
            <Label label="Start date" />
            <Controller
              control={control}
              name="startDate"
              render={({ field: { onChange, value: startDate } }) => {
                const endDate = watch('endDate');
                return (
                  <DatePicker
                    ref={setInputAttributeRef}
                    selectsStart
                    dateFormat="dd/MM/yyyy"
                    endDate={endDate}
                    minDate={new Date()}
                    minWidth="225px"
                    selected={startDate}
                    startDate={startDate}
                    onChange={(date) => {
                      onChange(date);
                    }}
                  />
                );
              }}
              rules={{ required: 'Start date required' }}
            />
            <ErrorMessage message={errors.startDate?.message?.toString()} />
          </Flex>

          <Flex column gap="var(--spacing-gap)">
            <Label label="End date" />
            <Controller
              control={control}
              name="endDate"
              render={({ field: { onChange, value: endDate } }) => {
                const startDate = watch('startDate');
                const isStartDateGreaterOrEqual = endDate
                  ? startDate?.getTime() >= endDate.getTime()
                  : false;
                return (
                  <DatePicker
                    ref={setInputAttributeRef}
                    selectsEnd
                    dateFormat="dd/MM/yyyy"
                    disabled={!!!startDate}
                    endDate={isStartDateGreaterOrEqual ? startDate : endDate}
                    minDate={startDate ?? new Date()}
                    minWidth="225px"
                    selected={isStartDateGreaterOrEqual ? startDate : endDate}
                    startDate={startDate ?? new Date()}
                    onChange={(date) => {
                      onChange(date);
                    }}
                  />
                );
              }}
              rules={{ required: 'End date required' }}
            />
            <ErrorMessage message={errors.endDate?.message?.toString()} />
          </Flex>
        </Flex>

        <Flex column alignItems="flex-start" gap="var(--spacing-gap)">
          <Label label="Brief description" />
          <TextArea
            maxLength={1000}
            resize="none"
            rows={7}
            style={{ minWidth: '225px', width: '100%' }}
            {...register('description', { required: 'Description required' })}
          />
          <ErrorMessage message={errors.description?.message?.toString()} />
        </Flex>
      </Flex>
    </form>
  );
};
